/**
 * FeedTutorialCoachmark — web port of the in-video tutorial coachmarks that iOS
 * drives from FeedNew.tsx:1129-1144 / 1383-1403 (the native SpotlightTutorial,
 * SpotlightTutorialController.swift buildWordClickSteps / buildPhraseHighlightSteps).
 *
 * iOS shows, for first-time users, two coachmark phases over the active feed
 * video — `word_click` then `phrase_highlight` — forcing captions on while they
 * run (setCaptionsExpanded(true)) and pausing playback (`!tutorialPhase` is part
 * of `shouldPlay`). After the word-click phase a Word-Color-Legend recap modal
 * appears (setShowRecapModal(true)). On native the steps complete by persisting
 * wordClickTutorialComplete / phraseHighlightTutorialComplete to user settings;
 * the web feed onboarding (tags / demo overlays) is instead localStorage-gated,
 * so we gate this coachmark the same way (first-time only), chained after the
 * FeedDemoOverlay exactly like the native phase ordering.
 *
 * The coachmark visuals (finger pointing at the first word / sweeping the first
 * line, pill above the transcript, target highlighted) are the shared
 * TranscriptCoachmark — the same component the lesson player uses. The overlay
 * here adds the feed's phase state machine + the tap/pan-anywhere advance,
 * mirroring iOS advanceMode .tapAnywhere (word_click) / .tapOrPanAnywhere
 * (phrase_highlight).
 */
import { useCallback, useEffect, useState } from "react";
import TranscriptCoachmark from "@/features/tutorial/components/TranscriptCoachmark";
import { type TranscriptCoachmarkPhase } from "@/features/tutorial/components/transcriptCoachmarkPhases";
import { captionsVisible$, isPaused$ } from "../state/videoState";
import WordStatesRecapModal from "./WordStatesRecapModal";

/** localStorage key — first-time-only gate, mirroring DEMO_SEEN_KEY / TAGS_SEEN_KEY. */
const COACHMARK_SEEN_KEY = "feed_word_tutorial_seen";

type Phase = TranscriptCoachmarkPhase | "recap";

interface FeedTutorialCoachmarkProps {
  /** Called once both phases complete (or the coachmark is dismissed). */
  onComplete: () => void;
}

function FeedTutorialCoachmark({ onComplete }: FeedTutorialCoachmarkProps) {
  const [phase, setPhase] = useState<Phase>("word_click");

  // Force captions on while the tutorial runs (iOS setCaptionsExpanded(true)
  // whenever tutorialPhase is set) and pause playback (iOS gates shouldPlay on
  // `!tutorialPhase`). The recap modal is its own pause point (iOS `!showRecapModal`).
  // On unmount we resume playback; captions stay on (they were forced on and the
  // user can still toggle them via the caption button), matching iOS which never
  // restores a prior "off" caption state mid-session.
  useEffect(() => {
    captionsVisible$.set(true);
    isPaused$.set(true);
    return () => {
      isPaused$.set(false);
    };
  }, []);

  const finish = useCallback(() => {
    localStorage.setItem(COACHMARK_SEEN_KEY, "true");
    onComplete();
  }, [onComplete]);

  // word_click: advance on a tap anywhere over the video (iOS .tapAnywhere) →
  // show the recap modal (iOS handleTutorialCompleted("word_click")).
  const advanceWordClick = useCallback(() => {
    setPhase("recap");
  }, []);

  // phrase_highlight: advance on a tap OR drag (iOS .tapOrPanAnywhere) → done
  // (iOS handleTutorialCompleted("phrase_highlight")).
  const advancePhraseHighlight = useCallback(() => {
    finish();
  }, [finish]);

  return (
    <>
      {/* Full-bleed gesture catcher. Transparent + tap/drag advances the phase,
          mirroring the native overlay's tap/pan-anywhere advance. The recap
          modal phase has no catcher (the modal owns its own dismissal). The
          coachmark visuals are pointer-events:none so they don't block it. */}
      {phase !== "recap" && (
        <div
          className="absolute inset-0 z-40"
          onPointerDown={
            phase === "word_click" ? advanceWordClick : advancePhraseHighlight
          }
          role="presentation"
        >
          <TranscriptCoachmark phase={phase} />
        </div>
      )}

      <WordStatesRecapModal
        isVisible={phase === "recap"}
        onContinue={() => setPhase("phrase_highlight")}
      />
    </>
  );
}

export { FeedTutorialCoachmark, COACHMARK_SEEN_KEY };
